import {
  CREATE_THUMBNAIL_TIMEOUT_MS,
  THUMBNAIL_MAX_SIDE,
} from "shared/attachments/constants";

export const isImage = (type: string): boolean => type.startsWith("image/");
export const isVideo = (type: string): boolean => type.startsWith("video/");

export const fitWithin = (
  width: number,
  height: number,
  maxSide: number
): { width: number; height: number } => {
  const longestSide = Math.max(width, height);
  if (longestSide <= maxSide) return { width, height };

  const scale = maxSide / longestSide;

  return {
    width: Math.round(width * scale),
    height: Math.round(height * scale),
  };
};

const drawToCanvas = (
  source: CanvasImageSource,
  width: number,
  height: number
): HTMLCanvasElement => {
  const target = fitWithin(width, height, THUMBNAIL_MAX_SIDE);
  const canvas = document.createElement("canvas");
  canvas.width = target.width;
  canvas.height = target.height;
  canvas.getContext("2d")?.drawImage(source, 0, 0, target.width, target.height);

  return canvas;
};

const canvasToBlob = (
  canvas: HTMLCanvasElement,
  type: string,
  quality?: number
): Promise<Blob | null> =>
  new Promise((resolve) => canvas.toBlob(resolve, type, quality));

const createImageThumbnail = async (
  file: File,
  targetType: string
): Promise<Blob | null> => {
  const bitmap = await createImageBitmap(file);
  try {
    const canvas = drawToCanvas(bitmap, bitmap.width, bitmap.height);

    return await canvasToBlob(canvas, targetType);
  } finally {
    bitmap.close();
  }
};

// The timeout lives inside this function so finish() always runs and the object
// URL is revoked no matter how the load ends.
const createVideoThumbnail = (
  file: File,
  targetType: string
): Promise<Blob | null> =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.muted = true;
    video.preload = "metadata";
    // iOS Safari refuses to decode a video it would have to play fullscreen, and
    // never fires `seeked` for one — so every video there would hit the timeout.
    video.playsInline = true;

    let settled = false;
    const finish = (result: Blob | null) => {
      if (settled) return;

      settled = true;
      clearTimeout(timeoutId);
      URL.revokeObjectURL(url);
      resolve(result);
    };

    const timeoutId = setTimeout(
      () => finish(null),
      CREATE_THUMBNAIL_TIMEOUT_MS
    );

    video.onloadedmetadata = () => {
      video.currentTime = Math.min(1, video.duration / 2);
    };

    video.onseeked = () => {
      canvasToBlob(
        drawToCanvas(video, video.videoWidth, video.videoHeight),
        targetType,
        0.8
      )
        .then(finish)
        .catch(() => finish(null));
    };

    video.onerror = () => finish(null);

    video.src = url;
    video.load();
  });

// Falls back to null on any failure or timeout, so a bad file never blocks an
// upload. The video path times out internally; only the image path needs the race.
export const createThumbnail = (
  file: File,
  targetType: string
): Promise<Blob | null> => {
  if (isVideo(file.type))
    return createVideoThumbnail(file, targetType).catch(() => null);

  if (!isImage(file.type)) return Promise.resolve(null);

  const timeout = new Promise<null>((resolve) => {
    setTimeout(() => resolve(null), CREATE_THUMBNAIL_TIMEOUT_MS);
  });

  return Promise.race([createImageThumbnail(file, targetType), timeout]).catch(
    () => null
  );
};
